import {
  ETIQUETA_FUENTE_DE_AGUA,
  ETIQUETA_OBRA_DE_AGUA,
  type TipoDeFuenteDeAgua,
  type TipoDeObraDeAgua,
} from "@/lib/informes";
import type {
  CampoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

import { fuenteDeAguaVacia, obraDeAguaVacia, type FilaDeFuenteDeAgua, type FilaDeObraDeAgua } from "./aguas";
import type { ModoDelTerreno } from "./cuadroDeConstrucciones";
import { fotosDelRegistro } from "./registroDeTerreno";

/* ---------------------------------------------------------------------------
   Lo que la pestaña "Descriptor de aguas" lee del registro de terreno: las
   fuentes y obras que el encargado creó en el predio, y los registros que
   cuelgan de cada fila del cuadro.

   Es lógica PURA y vive fuera del componente por la razón de siempre: decidir a
   qué fila pertenece un registro, o si una fila de terreno se ofrece para
   enlazar, son las reglas que pueden estar mal sin que nada se vea roto.
--------------------------------------------------------------------------- */

export type ClaseDeAgua = "fuente" | "obra";

/** Un ítem de terreno ya clasificado: sabemos su clase y su tipo del api. */
export interface ItemDeAguaPorEnlazar<T extends string> {
  uuid: string;
  item: ItemDeTerreno;
  tipo: T;
}

export interface ItemsPorEnlazar {
  fuentes: ItemDeAguaPorEnlazar<TipoDeFuenteDeAgua>[];
  obras: ItemDeAguaPorEnlazar<TipoDeObraDeAgua>[];
}

const PREFIJO_DE_TERRENO = "agua-terreno:";

/** La sección de aguas del registro de terreno, o `null`. */
export function seccionDeAguas(
  registro: RegistroDeTerrenoDelInforme | undefined,
): SeccionDeTerreno | null {
  return registro?.secciones.find((s) => s.clave === "aguas") ?? null;
}

/** Lo que el encargado dijo de la descripción general de aguas, o `null`. */
export function campoDeLaDescripcionDeAguas(
  registro: RegistroDeTerrenoDelInforme | undefined,
): CampoDeTerreno | null {
  return seccionDeAguas(registro)?.campos.find((c) => c.clave === "aguas") ?? null;
}

function dato(item: ItemDeTerreno, etiqueta: string): string {
  return item.datos.find((d) => d.etiqueta === etiqueta)?.valor ?? "";
}

function tipoPorEtiqueta<T extends string>(etiquetas: Record<T, string>, valor: string): T | null {
  const encontrado = (Object.keys(etiquetas) as T[]).find((tipo) => etiquetas[tipo] === valor);

  return encontrado ?? null;
}

/**
 * Los ítems de terreno que todavía no tienen fila en el cuadro (el servidor
 * cuelga los ya enlazados de `fuente:<id>`/`obra:<id>` y deja de listarlos
 * aparte), repartidos entre fuentes y obras.
 *
 * LA CLASE SALE DE LA ETIQUETA DEL TIPO que el servidor ya escribe en los
 * datos del ítem: sale de los mismos enums, así que un tipo que no se
 * reconoce es un ítem de una versión más nueva del servidor. Se ignora antes
 * que adivinar su clase — meter una obra en la lista de fuentes sumaría un
 * tranque al total de agua disponible.
 */
export function itemsPorEnlazarDeAguas(
  registro: RegistroDeTerrenoDelInforme | undefined,
): ItemsPorEnlazar {
  const resultado: ItemsPorEnlazar = { fuentes: [], obras: [] };

  for (const item of seccionDeAguas(registro)?.items ?? []) {
    if (!item.nuevo || !item.referencia.startsWith(PREFIJO_DE_TERRENO)) continue;

    const uuid = item.referencia.slice(PREFIJO_DE_TERRENO.length);
    const etiqueta = dato(item, "Tipo");

    const fuente = tipoPorEtiqueta(ETIQUETA_FUENTE_DE_AGUA, etiqueta);
    if (fuente) {
      resultado.fuentes.push({ uuid, item, tipo: fuente });
      continue;
    }

    const obra = tipoPorEtiqueta(ETIQUETA_OBRA_DE_AGUA, etiqueta);
    if (obra) resultado.obras.push({ uuid, item, tipo: obra });
  }

  return resultado;
}

/** "2 fuentes u obras creadas en terreno": el título del aviso, concordado. */
export function tituloDelAviso(cuantas: number): string {
  return cuantas === 1
    ? "1 fuente u obra creada en terreno"
    : `${cuantas} fuentes u obras creadas en terreno`;
}

/** La clave de la fila de borrador que nace de un ítem de terreno. */
export function claveDeTerreno(uuid: string): string {
  return `terreno-${uuid}`;
}

/**
 * Una fuente de terreno como fila del formulario: sin id, con el uuid como
 * `terreno_ref` y lo que el ítem ya sabe. NO nace validada.
 */
export function filaDeFuenteDesdeElItem(
  { uuid, item, tipo }: ItemDeAguaPorEnlazar<TipoDeFuenteDeAgua>,
): FilaDeFuenteDeAgua {
  return {
    ...fuenteDeAguaVacia(),
    clave: claveDeTerreno(uuid),
    tipo,
    rio_canal: dato(item, "Río o canal"),
    acciones: dato(item, "Acciones"),
    litros_seg: dato(item, "Caudal (l/s)"),
    terreno_ref: uuid,
  };
}

export function filaDeObraDesdeElItem(
  { uuid, item, tipo }: ItemDeAguaPorEnlazar<TipoDeObraDeAgua>,
): FilaDeObraDeAgua {
  return {
    ...obraDeAguaVacia(),
    clave: claveDeTerreno(uuid),
    tipo,
    capacidad_descripcion: dato(item, "Capacidad"),
    terreno_ref: uuid,
  };
}

/** Una fila que viene de terreno y todavía no se guardó: el servidor no la tiene. */
export function esBorradorDeTerreno(fila: { id: number | null; terreno_ref: string | null }): boolean {
  return fila.id === null && fila.terreno_ref !== null;
}

/**
 * El ítem de terreno cuyos registros se muestran bajo una fila.
 *
 * Una fila GUARDADA busca `fuente:<id>`/`obra:<id>` —ahí el servidor ya
 * colgó lo enlazado—; una de terreno sin guardar busca su propio
 * `agua-terreno:<uuid>`. Fuentes y obras tienen secuencias de id
 * independientes, por eso la clase es parte de la búsqueda.
 */
export function itemDeLaFila(
  items: ItemDeTerreno[],
  clase: ClaseDeAgua,
  fila: { id: number | null; terreno_ref: string | null },
): ItemDeTerreno | null {
  const referencia =
    fila.id !== null
      ? `${clase === "fuente" ? "fuente" : "obra"}:${fila.id}`
      : fila.terreno_ref !== null
        ? `${PREFIJO_DE_TERRENO}${fila.terreno_ref}`
        : null;

  if (referencia === null) return null;

  return items.find((i) => i.referencia === referencia) ?? null;
}

export interface ConteoDeRegistros {
  fotos: number;
  audios: number;
  textos: number;
  total: number;
}

export function conteoDeRegistrosDelItem(item: ItemDeTerreno | null): ConteoDeRegistros {
  const registros = item?.registros ?? [];
  const fotos = registros.reduce((n, r) => n + fotosDelRegistro(r).length, 0);
  const audios = registros.reduce((n, r) => n + r.audios.length, 0);
  const textos = registros.filter((r) => r.texto !== null).length;

  return { fotos, audios, textos, total: fotos + audios + textos };
}

/**
 * La palabra que acompaña al contador cuando NO hay nada que contar.
 *
 * "Visita pendiente" y "Sin registros" no son lo mismo: lo segundo afirma que
 * el encargado fue y no anotó nada. Con registros no se dice nada —hablan los
 * contadores— y con el módulo apagado tampoco.
 */
export function etiquetaDelCruceDeAgua(
  modo: ModoDelTerreno,
  conteo: ConteoDeRegistros,
): "Sin registros" | "Visita pendiente" | null {
  if (modo === "oculto" || conteo.total > 0) return null;

  return modo === "pendiente" ? "Visita pendiente" : "Sin registros";
}

/** Cómo se llama una fila en los selectores y en el encabezado de sus registros. */
export function tituloDeLaFila(
  clase: ClaseDeAgua,
  fila: { tipo: string; rio_canal?: string },
): string {
  if (clase === "fuente") {
    const canal = fila.rio_canal?.trim();
    if (canal) return canal;

    return ETIQUETA_FUENTE_DE_AGUA[fila.tipo as TipoDeFuenteDeAgua] ?? fila.tipo;
  }

  return ETIQUETA_OBRA_DE_AGUA[fila.tipo as TipoDeObraDeAgua] ?? fila.tipo;
}

export interface OpcionParaVincular {
  clave: string;
  etiqueta: string;
}

/**
 * Las filas con las que una fila de terreno puede decir "es la misma que":
 * las ya guardadas y que todavía no tienen un registro de terreno enlazado
 * (el servidor exige que cada uuid se use una sola vez).
 */
export function opcionesParaVincular(
  filas: (FilaDeFuenteDeAgua | FilaDeObraDeAgua)[],
): OpcionParaVincular[] {
  return filas
    .filter((fila) => fila.id !== null && fila.terreno_ref === null)
    .map((fila) => ({
      clave: fila.clave,
      etiqueta: tituloDeLaFila("rio_canal" in fila ? "fuente" : "obra", fila),
    }));
}
